import { API_BASE } from "../utils/api";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

// ── Default Categories ─────────────────────────────────────────────────────
const INCOME_CATEGORIES = [
  "Salary / Wages", "Freelance / Side Income", "Rental Income",
  "Investment Returns", "Business Income", "Other Income",
];

const EXPENSE_CATEGORIES = [
  "Housing (Rent/Bond)", "Groceries", "Transport / Fuel",
  "Utilities (Water/Electricity)", "Medical Aid", "Insurance",
  "Entertainment", "Clothing", "Education", "Savings / Investments",
  "Debt Repayments", "Other Expenses",
];

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const CURR_MONTH = MONTHS[new Date().getMonth()];
const CURR_YEAR  = new Date().getFullYear();

function fmt(n) {
  return "R " + Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(part, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((part / total) * 100));
}

const BAR_COLORS = [
  "bg-luxury-cta", "bg-luxury-gold", "bg-blue-500", "bg-purple-500",
  "bg-red-500", "bg-amber-500", "bg-teal-500", "bg-pink-500",
  "bg-indigo-500", "bg-orange-500", "bg-cyan-500", "bg-lime-500",
];

// Always use these via freshDefaults() — never set state to these directly
// (same reference causes React to skip re-renders when switching empty months)
const DEFAULT_INCOMES  = [{ id: 1, category: "Salary / Wages",      amount: "", note: "" }];
const DEFAULT_EXPENSES = [{ id: 1, category: "Housing (Rent/Bond)", amount: "", note: "" },
                           { id: 2, category: "Groceries",           amount: "", note: "" }];

// Deep copy so each reset gets a fresh reference React can detect
function freshIncomes()  { return DEFAULT_INCOMES.map(r  => ({ ...r })); }
function freshExpenses() { return DEFAULT_EXPENSES.map(r => ({ ...r })); }

export default function BudgetPlanner() {
  const { token }             = useAuth();
  const { addToast }          = useToast();
  const [month,    setMonth]    = useState(CURR_MONTH);
  const [year,     setYear]     = useState(CURR_YEAR);
  const [incomes,  setIncomes]  = useState(freshIncomes);
  const [expenses, setExpenses] = useState(freshExpenses);
  const [savingsGoal, setSavingsGoal] = useState("");
  const [activeTab,   setActiveTab]   = useState("overview");
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [savedId,  setSavedId]  = useState(null);
  const [isDirty,  setIsDirty]  = useState(false); // unsaved changes flag

  // ── Load budget from API when month/year changes ──────────────────────
  const loadBudget = useCallback(async (m, y) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/budget/${y}/${m}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.budget) {
          const b = data.budget;
          setIncomes(b.incomes.map((r, i) => ({ ...r, id: i + 1 })));
          setExpenses(b.expenses.map((r, i) => ({ ...r, id: i + 1 })));
          setSavingsGoal(b.savings_goal > 0 ? String(b.savings_goal) : "");
          setSavedId(b.id);
          setIsDirty(false);
        } else {
          // Fresh copies — forces React re-render even when switching between empty months
          setIncomes(freshIncomes());
          setExpenses(freshExpenses());
          setSavingsGoal("");
          setSavedId(null);
          setIsDirty(false);
        }
      }
    } catch (err) {
      console.error(err);
      addToast("Could not load budget — check your connection", "error");
    } finally {
      setLoading(false);
    }
  }, [token, addToast]);

  useEffect(() => { loadBudget(month, year); }, [month, year, loadBudget]);

  // ── Save budget to API ────────────────────────────────────────────────
  const saveBudget = async () => {
    setSaving(true);
    try {
      const body = {
        month, year,
        incomes:      incomes.map(({ category, amount, note }) => ({ category, amount: parseFloat(amount) || 0, note: note || "" })),
        expenses:     expenses.map(({ category, amount, note }) => ({ category, amount: parseFloat(amount) || 0, note: note || "" })),
        savings_goal: parseFloat(savingsGoal) || 0,
      };
      const res = await fetch(`${API_BASE}/api/budget/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedId(data.budget.id);
        setIsDirty(false);
        addToast(`Budget ${data.action === "created" ? "saved" : "updated"} successfully`, "success");
      } else {
        addToast("Failed to save — please try again", "error");
      }
    } catch (err) {
      addToast("Failed to save budget", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const totalIncome  = incomes.reduce((s, i)  => s + (parseFloat(i.amount)  || 0), 0);
  const totalExpense = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const netBalance   = totalIncome - totalExpense;
  const savingsRate  = totalIncome > 0 ? ((netBalance / totalIncome) * 100).toFixed(1) : 0;
  const goalAmt      = parseFloat(savingsGoal) || 0;
  const goalPct      = goalAmt > 0 ? pct(netBalance, goalAmt) : 0;

  const addRow = (type) => {
    const id = Date.now();
    setIsDirty(true);
    if (type === "income") {
      setIncomes(p => [...p, { id, category: INCOME_CATEGORIES[0], amount: "", note: "" }]);
    } else {
      setExpenses(p => [...p, { id, category: EXPENSE_CATEGORIES[0], amount: "", note: "" }]);
    }
  };

  const updateRow = (type, id, field, value) => {
    setIsDirty(true);
    if (type === "income") {
      setIncomes(p => p.map(r => r.id === id ? { ...r, [field]: value } : r));
    } else {
      setExpenses(p => p.map(r => r.id === id ? { ...r, [field]: value } : r));
    }
  };

  const removeRow = (type, id) => {
    setIsDirty(true);
    if (type === "income")  setIncomes(p => p.filter(r => r.id !== id));
    else                    setExpenses(p => p.filter(r => r.id !== id));
  };

  const reset = () => {
    if (!window.confirm("Reset all entries for this month?")) return;
    setIncomes(freshIncomes());
    setExpenses(freshExpenses());
    setSavingsGoal("");
    setSavedId(null);
  };

  // Group expenses by category for chart
  const expByCategory = expenses
    .filter(e => parseFloat(e.amount) > 0)
    .reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + parseFloat(e.amount);
      return acc;
    }, {});
  const expChartData = Object.entries(expByCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);


  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-luxury-muted">Personal Finance</span>
            </div>
            <h1 className="text-2xl font-bold text-luxury-text">Budget Planner</h1>
            <p className="text-sm text-luxury-secondary mt-1">Track your monthly income, expenses and savings goals.</p>
          </div>

          {/* Month / Year selector */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <select value={month} onChange={e => setMonth(e.target.value)}
              className="px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30">
              {MONTHS.map(m => <option key={m}>{m}</option>)}
            </select>
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30">
              {[2024,2025,2026].map(y => <option key={y}>{y}</option>)}
            </select>
            <button
              onClick={saveBudget}
              disabled={saving || loading}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-colors relative
                ${isDirty ? "bg-luxury-gold hover:bg-luxury-gold-dim" : "bg-luxury-cta hover:bg-luxury-cta-hover"} disabled:opacity-50`}
              title={isDirty ? "You have unsaved changes" : "Budget saved"}
            >
              {isDirty && !saving && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-luxury-bg" />
              )}
              {saving ? (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
              )}
              {saving ? "Saving..." : isDirty ? "Save Changes" : savedId ? "Saved ✓" : "Save Budget"}
            </button>
            <button onClick={reset} className="px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-muted hover:text-red-500 dark:hover:text-red-400 text-sm transition-colors" title="Reset month">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Loading indicator ────────────────────────────────────── */}
      {loading && (
        <div className="rounded-xl bg-luxury-surface border border-luxury-border px-5 py-3 mb-4 flex items-center gap-3">
          <svg className="animate-spin w-4 h-4 text-luxury-gold flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          <p className="text-xs text-luxury-secondary">Loading {month} {year} budget...</p>
        </div>
      )}

      {/* ── Summary Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Income",   value: fmt(totalIncome),   color: "text-luxury-cta",                          accent: "border-t-luxury-cta" },
          { label: "Total Expenses", value: fmt(totalExpense),  color: "text-red-600 dark:text-red-400",           accent: "border-t-red-400" },
          { label: "Net Balance",    value: fmt(netBalance),    color: netBalance >= 0 ? "text-luxury-cta" : "text-red-600 dark:text-red-400", accent: netBalance >= 0 ? "border-t-luxury-cta" : "border-t-red-400" },
          { label: "Savings Rate",   value: `${savingsRate}%`,  color: "text-luxury-gold",                         accent: "border-t-luxury-gold" },
        ].map(s => (
          <div key={s.label} className={`rounded-2xl p-5 border border-luxury-border border-t-2 bg-luxury-surface transition-all hover:scale-[1.02] ${s.accent}`}>
            <div className={`text-xl lg:text-2xl font-bold mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs font-medium text-luxury-secondary uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="flex gap-2 mb-6">
        {[
          { key: "overview",  label: "📊 Overview" },
          { key: "income",    label: "💰 Income" },
          { key: "expenses",  label: "💸 Expenses" },
          { key: "savings",   label: "🎯 Savings Goal" },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all
              ${activeTab === t.key
                ? "bg-luxury-cta text-white border-transparent"
                : "bg-luxury-surface border-luxury-border text-luxury-secondary hover:bg-luxury-elevated"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ─────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-5">

          {/* Income vs Expense bar */}
          <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
            <h3 className="font-semibold text-luxury-text text-sm mb-4">{month} {year} — Budget Overview</h3>
            <div className="space-y-4">
              {[
                { label: "Income",   value: totalIncome,  max: Math.max(totalIncome, totalExpense, 1), color: "bg-luxury-cta",              textColor: "text-luxury-cta" },
                { label: "Expenses", value: totalExpense, max: Math.max(totalIncome, totalExpense, 1), color: "bg-red-500",                 textColor: "text-red-600 dark:text-red-400" },
                { label: "Savings",  value: Math.max(0, netBalance), max: Math.max(totalIncome, 1), color: "bg-luxury-gold",  textColor: "text-luxury-gold" },
              ].map(bar => (
                <div key={bar.label}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide">{bar.label}</span>
                    <span className={`text-sm font-bold ${bar.textColor}`}>{fmt(bar.value)}</span>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-luxury-elevated overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${bar.color}`}
                      style={{ width: `${pct(bar.value, bar.max)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expense Breakdown Chart */}
          {expChartData.length > 0 && (
            <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
              <h3 className="font-semibold text-luxury-text text-sm mb-4">Expense Breakdown</h3>
              <div className="space-y-3">
                {expChartData.map(([cat, amt], idx) => (
                  <div key={cat}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-luxury-secondary truncate max-w-[60%]">{cat}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-luxury-text">{fmt(amt)}</span>
                        <span className="text-xs text-luxury-muted">{pct(amt, totalExpense)}%</span>
                      </div>
                    </div>
                    <div className="w-full h-2 rounded-full bg-luxury-elevated overflow-hidden">
                      <div className={`h-full rounded-full ${BAR_COLORS[idx % BAR_COLORS.length]}`}
                        style={{ width: `${pct(amt, totalExpense)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Health Summary */}
          <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
            <h3 className="font-semibold text-luxury-text text-sm mb-4">Budget Health</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                {
                  label: "Expense Ratio",
                  value: `${totalIncome > 0 ? pct(totalExpense, totalIncome) : 0}%`,
                  note: "of income spent",
                  ok: totalIncome > 0 && totalExpense / totalIncome < 0.8,
                },
                {
                  label: "Savings Rate",
                  value: `${savingsRate}%`,
                  note: "of income saved",
                  ok: parseFloat(savingsRate) >= 20,
                },
                {
                  label: "Balance",
                  value: netBalance >= 0 ? "Positive" : "Negative",
                  note: netBalance >= 0 ? "You are within budget" : "Spending exceeds income",
                  ok: netBalance >= 0,
                },
              ].map(h => (
                <div key={h.label} className={`rounded-xl p-4 border ${h.ok ? "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10" : "border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/10"}`}>
                  <p className="text-xs font-semibold text-luxury-muted uppercase tracking-wide mb-1">{h.label}</p>
                  <p className={`text-xl font-bold ${h.ok ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>{h.value}</p>
                  <p className="text-xs text-luxury-secondary mt-0.5">{h.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── INCOME TAB ───────────────────────────────────────────── */}
      {activeTab === "income" && (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border overflow-hidden">
          <div className="px-5 py-4 border-b border-luxury-border bg-luxury-elevated flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-luxury-text text-sm">Income Sources</h3>
              <p className="text-xs text-luxury-muted mt-0.5">Total: <span className="text-luxury-cta font-bold">{fmt(totalIncome)}</span></p>
            </div>
            <button onClick={() => addRow("income")}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-cta hover:bg-luxury-cta-hover text-white transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Row
            </button>
          </div>
          <div className="divide-y divide-luxury-border">
            {incomes.map(row => (
              <div key={row.id} className="flex items-center gap-3 px-5 py-3">
                <select value={row.category} onChange={e => updateRow("income", row.id, "category", e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-xs focus:outline-none focus:ring-2 focus:ring-luxury-gold/30">
                  {INCOME_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
                <input type="number" placeholder="Amount (R)" value={row.amount}
                  onChange={e => updateRow("income", row.id, "amount", e.target.value)}
                  className="w-36 px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-xs focus:outline-none focus:ring-2 focus:ring-luxury-cta/30 placeholder-luxury-muted" />
                <input type="text" placeholder="Note (optional)" value={row.note}
                  onChange={e => updateRow("income", row.id, "note", e.target.value)}
                  className="hidden sm:block flex-1 px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-xs focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted" />
                <button onClick={() => removeRow("income", row.id)}
                  className="p-1.5 rounded-lg text-luxury-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 bg-luxury-elevated border-t border-luxury-border flex justify-between items-center">
            <span className="text-xs font-semibold text-luxury-muted uppercase tracking-wide">Total Income</span>
            <span className="text-base font-bold text-luxury-cta">{fmt(totalIncome)}</span>
          </div>
        </div>
      )}

      {/* ── EXPENSES TAB ─────────────────────────────────────────── */}
      {activeTab === "expenses" && (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border overflow-hidden">
          <div className="px-5 py-4 border-b border-luxury-border bg-luxury-elevated flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-luxury-text text-sm">Monthly Expenses</h3>
              <p className="text-xs text-luxury-muted mt-0.5">Total: <span className="text-red-600 dark:text-red-400 font-bold">{fmt(totalExpense)}</span></p>
            </div>
            <button onClick={() => addRow("expense")}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-cta hover:bg-luxury-cta-hover text-white transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Row
            </button>
          </div>
          <div className="divide-y divide-luxury-border">
            {expenses.map(row => (
              <div key={row.id} className="flex items-center gap-3 px-5 py-3">
                <select value={row.category} onChange={e => updateRow("expense", row.id, "category", e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-xs focus:outline-none focus:ring-2 focus:ring-luxury-gold/30">
                  {EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
                <input type="number" placeholder="Amount (R)" value={row.amount}
                  onChange={e => updateRow("expense", row.id, "amount", e.target.value)}
                  className="w-36 px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-xs focus:outline-none focus:ring-2 focus:ring-red-500/30 placeholder-luxury-muted" />
                <input type="text" placeholder="Note (optional)" value={row.note}
                  onChange={e => updateRow("expense", row.id, "note", e.target.value)}
                  className="hidden sm:block flex-1 px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-xs focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted" />
                <button onClick={() => removeRow("expense", row.id)}
                  className="p-1.5 rounded-lg text-luxury-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 bg-luxury-elevated border-t border-luxury-border flex justify-between items-center">
            <span className="text-xs font-semibold text-luxury-muted uppercase tracking-wide">Total Expenses</span>
            <span className="text-base font-bold text-red-600 dark:text-red-400">{fmt(totalExpense)}</span>
          </div>
        </div>
      )}

      {/* ── SAVINGS GOAL TAB ─────────────────────────────────────── */}
      {activeTab === "savings" && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
            <h3 className="font-semibold text-luxury-text text-sm mb-4">Set Your Savings Goal for {month} {year}</h3>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-luxury-muted">R</span>
                <input type="number" value={savingsGoal} onChange={e => { setSavingsGoal(e.target.value); setIsDirty(true); }}
                  placeholder="e.g. 5000"
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted" />
              </div>
            </div>
          </div>

          {goalAmt > 0 && (
            <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-luxury-text text-sm">Goal Progress</h3>
                <span className="text-2xl font-bold text-luxury-gold">{goalPct}%</span>
              </div>
              <div className="w-full h-4 rounded-full bg-luxury-elevated overflow-hidden mb-3">
                <div className={`h-full rounded-full transition-all duration-700 ${goalPct >= 100 ? "bg-luxury-cta" : "bg-luxury-gold"}`}
                  style={{ width: `${goalPct}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Goal",      value: fmt(goalAmt),                              color: "text-luxury-gold" },
                  { label: "Saved",     value: fmt(Math.max(0, netBalance)),              color: "text-luxury-cta" },
                  { label: "Remaining", value: fmt(Math.max(0, goalAmt - netBalance)),    color: netBalance >= goalAmt ? "text-luxury-cta" : "text-amber-600 dark:text-amber-400" },
                ].map(s => (
                  <div key={s.label} className="text-center p-3 rounded-xl bg-luxury-elevated border border-luxury-border">
                    <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-luxury-secondary mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
              {netBalance >= goalAmt && (
                <div className="mt-4 rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10 px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">🎉 Savings goal achieved for {month}!</p>
                  <p className="text-xs text-luxury-secondary mt-0.5">You are R{Math.round(netBalance - goalAmt).toLocaleString()} above your target.</p>
                </div>
              )}
            </div>
          )}

          {/* SA savings tips */}
          <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
            <h3 className="font-semibold text-luxury-text text-sm mb-3">💡 South Africa Savings Tips</h3>
            <div className="space-y-2">
              {[
                "Aim to save at least 20% of your monthly income",
                "Max out your Retirement Annuity — up to 27.5% is tax-deductible",
                "Use a Tax-Free Savings Account (TFSA) — R36,000/year tax-free",
                "Pay off high-interest debt before investing",
                "Keep 3–6 months of expenses as an emergency fund",
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-luxury-elevated flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-luxury-gold">{i + 1}</span>
                  </span>
                  <p className="text-xs text-luxury-secondary">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
